import {useMergeRefs} from '@chakra-ui/react';
import {ChakraLi} from '@linx-security/ui/utils/chakra-elements';
import {dataAttr} from '@utils';
import {OverlayScrollbarsComponent, type OverlayScrollbarsComponentRef} from 'overlayscrollbars-react';
import {type ListChildComponentProps} from 'react-window';
import {useEffect, useRef, type ComponentType, type ReactNode, type CSSProperties, forwardRef} from 'react';

import {parts} from '../theme/anatomy';

export const DEFAULT_HEIGHT = 34;
export const DEFAULT_WIDTH = 248;
// Add padding between items
export const Row: ComponentType<
  ListChildComponentProps<any> & {
    children: ReactNode;
    setRowHeight: (index: number, height: number) => void;
    isFirst: boolean;
    isLast: boolean;
  }
> = ({style: {height, ...style}, children, setRowHeight, index, isFirst, isLast}) => {
  const ref = useRef<HTMLLIElement | null>(null);
  const isMeasured = useRef<boolean>(false);
  useEffect(() => {
    if (ref.current && !isMeasured.current) {
      isMeasured.current = true;
      setRowHeight(index, ref.current.clientHeight);
    }
    // eslint-disable-next-line
  }, [ref]);

  return (
    <ChakraLi
      ref={ref}
      data-first={dataAttr(isFirst)}
      data-last={dataAttr(isLast)}
      {...parts.option.attrs}
      style={{
        display: `flex`,
        flexWrap: `nowrap`,
        overflowX: `hidden`,
        listStyleType: `none`,
        ...style,
        ...(isMeasured.current ? {height} : {}),
      }}>
      {children}
    </ChakraLi>
  );
};

export const CustomScrollbars = forwardRef(
  ({children, onScroll, style}: {children: ReactNode; onScroll: () => void; style: CSSProperties}, ref) => {
    const ofRef = useRef<OverlayScrollbarsComponentRef | null>(null);
    const mergedRef = useMergeRefs(ref, ofRef);
    useEffect(() => {
      const el = ofRef.current?.osInstance()?.elements().viewport;

      if (onScroll && el) el.addEventListener(`scroll`, onScroll);

      return () => {
        if (onScroll && el) el.removeEventListener(`scroll`, onScroll);
      };
    }, [onScroll]);

    return (
      <OverlayScrollbarsComponent options={{scrollbars: {autoHide: `move`}}} ref={mergedRef} style={style}>
        {children}
      </OverlayScrollbarsComponent>
    );
  },
);
